import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { approvalSection } from "@/lib/sectionApproval";
import { staffTitles } from "@/lib/staffTitles";

export default function RecordApproval({ section, counselorId = null, counselorGender = "", contentCount = 0 }) {
  const { user } = useAuth();
  const ownerId = counselorId || user?.id;
  const sectionKey = approvalSection(section);
  const allowed = ["principal", "counselor", "admin"].includes(user?.role);
  const path = `/section-approvals/${encodeURIComponent(ownerId)}/${encodeURIComponent(sectionKey)}`;
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const container = useRef(null);
  const anchor = `approval-${sectionKey}`;
  useEffect(() => {
    if (!allowed || !ownerId) return;
    let active = true;
    setLoading(true);
    setReview(null);
    setError("");
    api.get(path).then(({ data }) => { if (active) setReview(data); })
      .catch((failure) => { if (active) setError(formatErr(failure)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [allowed, ownerId, path, contentCount, refresh]);
  useEffect(() => {
    if (window.location.hash === `#${encodeURIComponent(anchor)}` || window.location.hash === `#${anchor}`) {
      const target = container.current;
      if (target && target.getClientRects().length) {
        target.scrollIntoView({ block: "center" });
        target.focus({ preventScroll: true });
      }
    }
  }, [anchor, loading]);
  if (!allowed || !ownerId) return null;
  if (!loading && !error && !review?.has_content && !review?.principal_approval) return null;
  const approval = review?.principal_approval;
  const current = Boolean(approval);
  const gender = approval?.principal_gender || (user?.role === "principal" ? user.gender : counselorGender || (user?.role === "counselor" ? user.gender : ""));
  const title = staffTitles(gender).principal;
  const approve = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(path);
      setReview(data);
      toast.success("تم اعتماد قسم السجل مرة واحدة لهذا المرشد");
    } catch (failure) {
      const message = formatErr(failure);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div ref={container} id={anchor} tabIndex={-1} className="no-print space-y-2 rounded-lg border border-[#e6e2d8] bg-[#f7faf8] p-3 text-sm" data-testid={`section-approval-${sectionKey}`}>
      <div className={current ? "text-[#27624d]" : "text-[#805B12]"}>
        {loading ? "جاري تحميل اعتماد القسم..." : current ? `${gender === "female" ? "اطّلعت" : "اطّلع"} ${title} ${gender === "female" ? "واعتمدت" : "واعتمد"} قسم السجل` : !error && `${gender === "female" ? "لم تعتمد" : "لم يعتمد"} ${title} قسم السجل بعد`}
      </div>
      {approval && <div>{approval.principal_name} — {new Date(approval.approved_at).toLocaleString("ar-SA")}</div>}
      {!loading && !error && review?.has_content && <p>اعتماد واحد للقسم كاملًا؛ لا يتطلب اعتمادًا جديدًا عند إضافة أو تعديل الإدخالات.</p>}
      {user?.role === "principal" && !loading && review?.has_content && !current && (
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={approve} data-testid={`approve-section-${sectionKey}`}>
          {saving ? "جاري الاعتماد..." : "اطلعت واعتمدت قسم السجل"}
        </Button>
      )}
      {error && <><p role="alert" className="text-red-700">{error}</p><Button type="button" variant="outline" disabled={saving || loading} onClick={() => setRefresh((value) => value + 1)}>إعادة المحاولة</Button></>}
    </div>
  );
}
